/**
 * Onboarding (2026-10-27): the invited colleague's welcome and tour, the
 * getting-started checklist, the workspace wizard, invites.
 *
 * API: `api/Modules/Onboarding` (OnboardingController, OnboardingAdminController)
 * and the two anonymous invite legs in AuthController (`/auth/invite`,
 * `/auth/accept-invite`). Every read degrades: while
 * db/2026-10-27_onboarding.sql is missing, `/onboarding/me` answers
 * `available: false` and nothing is shown.
 */
import { apiFetch, apiFetchRaw } from '@/auth/api';

export const ONBOARDING_ME_KEY = ['onboarding', 'me'] as const;
export const ONBOARDING_PROFILE_KEY = ['onboarding', 'profile'] as const;
export const WORKSPACE_SETUP_KEY = ['onboarding', 'workspace'] as const;

export interface ChecklistItem {
  id: string;
  label: string;
  hint: string;
  done: boolean;
  to: string;
  optional: boolean;
}

export interface Checklist {
  kind: 'workspace' | 'person';
  items: ChecklistItem[];
  allDone: boolean;
}

export type StepState = 'done' | 'skipped';

export interface WorkspaceState {
  status: 'pending' | 'done';
  autoOpen: boolean;
  startedAt: string | null;
  completedAt: string | null;
  checklistDismissedAt: string | null;
  steps: Record<string, StepState>;
  /** No row and not a new workspace (Asian Trails): set up, nothing shows by itself. */
  legacy: boolean;
}

export interface OnboardingMe {
  available: boolean;
  welcome?: { required: boolean; done: boolean };
  tour?: { done: boolean };
  invited?: boolean;
  checklist?: Checklist | null;
  /** Admins only. */
  workspace?: WorkspaceState | null;
}

export interface OwnProfile {
  email: string | null;
  fullName: string | null;
  position: string | null;
  ownPhone: string | null;
  photoUrl: string | null;
  positionAvailable: boolean;
  phoneAvailable: boolean;
}

export interface InviteInput {
  email: string;
  fullName?: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  teamId?: string | null;
  allowedTools: string[];
  position?: string;
}

export interface InviteResult {
  ok: boolean;
  error: string | null;
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  emailSent?: boolean;
  /** Handed back once so the admin can pass it on by hand (mail is suppressed in the demo). */
  inviteUrl?: string | null;
}

export interface BrandProbeResult {
  ok: boolean;
  simulated: boolean;
  website: string;
  name: string | null;
  logos: string[];
  accent: string | null;
  message: string | null;
}

export interface InvitePeek {
  firstName: string | null;
  company: string;
  minLength: number;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});

export const onboardingApi = {
  me: async (signal?: AbortSignal): Promise<OnboardingMe> => {
    try {
      return await apiFetch<OnboardingMe>('/onboarding/me', { signal });
    } catch {
      // An older API, or a blip: no onboarding, never an error on screen.
      return { available: false };
    }
  },
  profile: (signal?: AbortSignal) => apiFetch<OwnProfile>('/onboarding/me/profile', { signal }),
  saveProfile: (patch: { fullName?: string; position?: string; ownPhone?: string }) =>
    apiFetchRaw<{ ok: boolean }>('/onboarding/me/profile', json('PUT', patch), { mutation: true }),
  uploadPhoto: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiFetchRaw<{ ok: boolean; url: string }>('/onboarding/me/photo', { method: 'POST', body: fd }, { mutation: true });
  },
  welcomeDone: () => apiFetchRaw<{ ok: boolean }>('/onboarding/me/welcome', json('POST', {}), { mutation: true }),
  tourDone: () => apiFetchRaw<{ ok: boolean }>('/onboarding/me/tour', json('POST', {}), { mutation: true }),
  /** The person opened Quotinc from Amassa (linked installs only): ticks "Open Quotinc". */
  quotincOpened: () =>
    apiFetchRaw<{ ok: boolean }>('/onboarding/me/quotinc-opened', json('POST', {}), { mutation: true }),
  dismissChecklist: (kind: 'workspace' | 'person') =>
    apiFetchRaw<{ ok: boolean }>('/onboarding/me/checklist/dismiss', json('POST', { kind }), { mutation: true }),

  // ── Admin ──
  invite: (invites: InviteInput[]) =>
    apiFetchRaw<{ ok: boolean; results: InviteResult[] }>('/admin/onboarding/invites', json('POST', { invites }), {
      mutation: true,
    }),
  resendInvite: (userId: string) =>
    apiFetchRaw<{ ok: boolean; result: InviteResult }>(
      `/admin/onboarding/invites/${encodeURIComponent(userId)}/resend`,
      json('POST', {}),
      { mutation: true },
    ),
  resetPerson: (userId: string) =>
    apiFetchRaw<{ ok: boolean }>(`/admin/onboarding/users/${encodeURIComponent(userId)}/reset`, json('POST', {}), {
      mutation: true,
    }),
  workspace: (signal?: AbortSignal) =>
    apiFetch<{ state: WorkspaceState; items: ChecklistItem[]; allDone: boolean }>('/admin/onboarding/workspace', {
      signal,
    }),
  saveWorkspace: (patch: { step?: string; stepState?: StepState | null; status?: 'pending' | 'done'; start?: boolean }) =>
    apiFetch<WorkspaceState>('/admin/onboarding/workspace', json('PUT', patch), { mutation: true }),
  resetWorkspace: () => apiFetch<WorkspaceState>('/admin/onboarding/workspace/reset', json('POST', {}), { mutation: true }),
  brandProbe: (website: string) =>
    apiFetch<BrandProbeResult>('/admin/onboarding/brand-probe', json('POST', { website }), { mutation: true }),

  // ── Anonymous: the set-password page ──
  peekInvite: (token: string, signal?: AbortSignal) =>
    apiFetch<InvitePeek>(`/auth/invite?token=${encodeURIComponent(token)}`, { signal }),
  acceptInvite: (token: string, password: string) =>
    apiFetchRaw<{ ok: boolean }>('/auth/accept-invite', json('POST', { token, password }), { mutation: true }),
};

/** Ask the shell to (re)start the tour from anywhere (Preferences ▸ Help, the checklist). */
export const TOUR_EVENT = 'amassa:tour';
export function startTour(): void {
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}
